/**
 * CapabilityCompactList — lista compacta de capacidades (patrón tabla del hive).
 *
 * ISS-572 / REQ-CAP-002 §4/§5: vista resumen de las tres superficies; la
 * edición vive en `CapabilitiesModal`. Muestra icono, título, categoría,
 * estado efectivo, origen (herencia) y resumen de settings.
 */

import { Badge } from '@/components/ui';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableEmpty,
} from '@/components/ui';
import { useI18n } from '@/i18n';
import {
  CapabilityIcon,
  capabilityCategory,
  capabilityTitle,
  OriginBadge,
} from './CapabilityRow';
import type { CapabilityCatalogEntry, EffectiveCapability } from './types';

interface Props {
  catalog: CapabilityCatalogEntry[];
  effective: Record<string, EffectiveCapability>;
}

export function CapabilityCompactList({ catalog, effective }: Props) {
  const { t } = useI18n();

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('admin.capabilities.columns.capability') || 'Capability'}</TableHead>
          <TableHead>{t('admin.capabilities.columns.category') || 'Category'}</TableHead>
          <TableHead>{t('admin.capabilities.columns.status') || 'Status'}</TableHead>
          <TableHead>{t('admin.capabilities.columns.origin') || 'Origin'}</TableHead>
          <TableHead>{t('admin.capabilities.columns.settings') || 'Settings'}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {catalog.length === 0 ? (
          <TableEmpty colSpan={5} message={t('admin.capabilities.loadError') || 'No capabilities.'} />
        ) : (
          catalog.map((entry) => {
            const eff = effective[entry.slug];
            const enabled = eff?.enabled ?? false;
            const settingsKeys = eff ? Object.keys(eff.settings ?? {}) : [];
            return (
              <TableRow key={entry.slug}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                      <CapabilityIcon entry={entry} className="h-3.5 w-3.5" />
                    </span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {capabilityTitle(t, entry)}
                    </span>
                    {entry.platform_active === false && (
                      <Badge variant="destructive">
                        {t('admin.capabilities.platformOff') || 'Not available on the platform'}
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline">{capabilityCategory(t, entry)}</Badge>
                </TableCell>
                <TableCell>
                  <Badge variant={enabled ? 'default' : 'secondary'}>
                    {enabled
                      ? t('admin.capabilities.decision.enabled') || 'Enabled'
                      : t('admin.capabilities.decision.disabled') || 'Disabled'}
                  </Badge>
                </TableCell>
                <TableCell>{eff ? <OriginBadge origin={eff.origin} /> : '—'}</TableCell>
                <TableCell className="text-xs text-gray-500 dark:text-gray-400">
                  {settingsKeys.length > 0
                    ? t('admin.capabilities.settingsSummary', { count: settingsKeys.length }) ||
                      `Settings: ${settingsKeys.length}`
                    : t('admin.capabilities.settingsNone') || '—'}
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}
